import { ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMockAdmissionList } from "../../components/ipd/admission/AdmittedPatientsModal";

const BED_IMAGES = {
  empty: `${process.env.PUBLIC_URL || ""}/images/beds/bed_illustration.png`,
  male: `${process.env.PUBLIC_URL || ""}/images/beds/bed_male.png`,
  female: `${process.env.PUBLIC_URL || ""}/images/beds/bed_female.png`,
};

const WARD_BED_COUNTS = {
  "ICU I": 6,
  ICU: 6,
  "Private Room": 20,
  "General Ward": 24,
};

const getBedImage = (bed) => {
  if (!bed.occupied) return BED_IMAGES.empty;
  const gender = (bed.gender || "").toLowerCase();
  if (gender === "female") return BED_IMAGES.female;
  return BED_IMAGES.male;
};

const BedCard = ({ bed }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const statusColor = bed.occupied ? "bg-red-500" : "bg-green-500";

  return (
    <div className="flex flex-col items-center rounded-xl border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition-shadow">
      <div className="relative mb-2 h-16 w-20 flex items-center justify-center">
        {!imageFailed ? (
          <img
            src={getBedImage(bed)}
            alt={bed.occupied ? `Bed ${bed.bedNo} occupied` : `Bed ${bed.bedNo} available`}
            className="h-16 w-20 object-contain"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <span className="text-4xl" aria-hidden>
            🛏️
          </span>
        )}
        <span
          className={`absolute -top-1 -right-1 h-3 w-3 rounded-full border-2 border-white ${statusColor}`}
          title={bed.occupied ? "Booked" : "Available"}
        />
      </div>
      <p className="text-xs font-semibold text-[#217E90]">Bed {bed.bedNo}</p>
      {bed.occupied ? (
        <p className="mt-1 text-[10px] text-gray-600 text-center line-clamp-2">
          {bed.patientName}
        </p>
      ) : (
        <p className="mt-1 text-[10px] text-green-600">Available</p>
      )}
    </div>
  );
};

function BedView() {
  const navigate = useNavigate();

  const wards = useMemo(() => {
    const admissions = getMockAdmissionList().filter((row) => row.status === "Admitted");
    const occupiedByWard = {};

    admissions.forEach((row) => {
      const ward = row.wardName || "General Ward";
      if (!occupiedByWard[ward]) occupiedByWard[ward] = {};
      occupiedByWard[ward][String(row.bedNo)] = row;
    });

    return Object.entries(WARD_BED_COUNTS).map(([wardName, totalBeds]) => {
      const occupied = occupiedByWard[wardName] || {};
      const beds = Array.from({ length: totalBeds }, (_, index) => {
        const bedNo = String(index + 1);
        const admission = occupied[bedNo];
        return {
          bedNo,
          occupied: Boolean(admission),
          patientName: admission?.patientName || "",
          gender: admission?.gender || "",
        };
      });

      const booked = beds.filter((bed) => bed.occupied).length;
      return { wardName, beds, booked, available: totalBeds - booked };
    });
  }, []);

  return (
    <div className="p-2 sm:p-3 pb-20 min-h-[calc(100vh-1rem)] flex flex-col">
      <div className="bg-white rounded-lg shadow-sm border flex flex-col min-h-0 flex-1">
        <div className="sticky top-0 z-10 bg-white rounded-t-lg px-2 py-1 sm:px-3 sm:py-1.5 border-b border-gray-200 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm sm:text-base font-semibold text-[#217E90] flex items-center gap-1 sm:gap-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="p-0.5 rounded hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-[#217E90]"
                aria-label="Go back"
              >
                <ArrowUturnLeftIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
              </button>
              <span className="truncate">Bed View</span>
            </h2>
            <div className="flex items-center gap-3 text-xs text-gray-600">
              <span className="flex items-center gap-1">
                <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
                Available
              </span>
              <span className="flex items-center gap-1">
                <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
                Booked
              </span>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-6">
          {wards.map((ward) => (
            <section key={ward.wardName}>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm sm:text-base font-semibold text-[#563621]">
                  {ward.wardName}
                </h3>
                <p className="text-xs text-gray-500">
                  {ward.booked} booked · {ward.available} available
                </p>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                {ward.beds.map((bed) => (
                  <BedCard key={`${ward.wardName}-${bed.bedNo}`} bed={bed} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

export default BedView;
